/**
 * Tag / label DSL parser for `pending-tag-search-saved-views`.
 *
 * The single source of truth: the same parser runs on the API
 * (translating into a SQL `WHERE` clause) and on the frontend
 * (driving the search bar + saved-view validator). Living in
 * `@rynora/contracts` keeps the two parsers byte-for-byte identical.
 *
 * Grammar (informal):
 *
 *   query    := expr
 *   expr     := and (OR and)*
 *   and      := not (AND? not)*      // AND is implicit between terms
 *   not      := 'NOT' not | atom
 *   atom     := '(' expr ')' | field | freeText
 *   field    := key ':' value
 *   key      := 'tag' | 'env' | 'owner' | 'status' | 'name' | 'is'
 *   value    := bareWord | quoted
 *   freeText := bareWord | quoted      // matches against name (flow) / status (run) / ...
 *
 * Tokens are case-insensitive for keywords (`AND` / `OR` / `NOT`
 * are also accepted lowercase). Field keys are case-insensitive
 * and lowercased on parse. Field values keep their case so
 * `owner:Auth-Team` doesn't break against a case-sensitive field.
 *
 * Strings can be double-quoted (`name:"My Critical Flow"`); inside
 * quotes only `\"` and `\\` are escape sequences. Trailing
 * whitespace is allowed (the parser eats it).
 *
 * v2 widening (NOT in this parser):
 *   • Numeric comparisons (`duration:>5s`).
 *   • Date ranges (`updated:>2026-05-01`).
 *   • Glob matching on values (`tag:smoke-*`).
 */

/** Tokens used by the lexer. Public so the test file can spy on them. */
export type Token =
  | { type: 'word'; value: string; pos: number }
  | { type: 'quoted'; value: string; pos: number }
  | { type: 'colon'; pos: number }
  | { type: 'lparen'; pos: number }
  | { type: 'rparen'; pos: number }
  | { type: 'and'; pos: number }
  | { type: 'or'; pos: number }
  | { type: 'not'; pos: number };

/** The AST. */
export type TagDslAst =
  | { kind: 'and'; left: TagDslAst; right: TagDslAst }
  | { kind: 'or'; left: TagDslAst; right: TagDslAst }
  | { kind: 'not'; inner: TagDslAst }
  | { kind: 'field'; key: string; value: string }
  | { kind: 'free'; value: string };

export type ParseTagDslResult = { ok: true; ast: TagDslAst } | { ok: false; error: string; pos: number };

export const FIELD_KEYS = ['tag', 'env', 'owner', 'status', 'name', 'is'] as const;
export type FieldKey = (typeof FIELD_KEYS)[number];
const FIELD_KEY_SET = new Set<string>(FIELD_KEYS);

/** Tokenizer — converts the raw DSL string into a list of tokens. */
export function tokenizeTagDsl(input: string): { ok: true; tokens: Token[] } | { ok: false; error: string; pos: number } {
  const tokens: Token[] = [];
  let i = 0;
  const N = input.length;

  while (i < N) {
    const ch = input[i];

    // Whitespace.
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }

    // Punctuation.
    if (ch === '(') {
      tokens.push({ type: 'lparen', pos: i });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', pos: i });
      i++;
      continue;
    }
    if (ch === ':') {
      tokens.push({ type: 'colon', pos: i });
      i++;
      continue;
    }

    // Quoted strings.
    if (ch === '"') {
      const start = i;
      i++;
      let out = '';
      while (i < N) {
        const c = input[i];
        if (c === '\\' && i + 1 < N) {
          const next = input[i + 1];
          if (next === '"' || next === '\\') {
            out += next;
            i += 2;
            continue;
          }
          // Unknown escape — leave the backslash literal.
          out += c;
          i++;
          continue;
        }
        if (c === '"') {
          tokens.push({ type: 'quoted', value: out, pos: start });
          i++;
          break;
        }
        out += c;
        i++;
      }
      if (i > N) {
        return { ok: false, error: 'Unterminated quoted string.', pos: start };
      }
      // If we fell off the end without closing the quote.
      if (i === N && input[i - 1] !== '"') {
        return { ok: false, error: 'Unterminated quoted string.', pos: start };
      }
      continue;
    }

    // Bare word — runs until whitespace, `(`, `)`, or `:`.
    if (isWordStart(ch)) {
      const start = i;
      while (i < N && isWordChar(input[i])) i++;
      const word = input.substring(start, i);
      const upper = word.toUpperCase();
      if (upper === 'AND') tokens.push({ type: 'and', pos: start });
      else if (upper === 'OR') tokens.push({ type: 'or', pos: start });
      else if (upper === 'NOT') tokens.push({ type: 'not', pos: start });
      else tokens.push({ type: 'word', value: word, pos: start });
      continue;
    }

    return { ok: false, error: `Unexpected character '${ch}'.`, pos: i };
  }

  return { ok: true, tokens };
}

function isWordStart(c: string): boolean {
  return /[A-Za-z0-9_]/.test(c);
}

function isWordChar(c: string): boolean {
  return /[A-Za-z0-9_\-./*@]/.test(c);
}

/**
 * Parse the DSL string into an AST. The recursive-descent parser
 * mirrors the grammar comment at the top of this file.
 */
export function parseTagDsl(input: string): ParseTagDslResult {
  const lex = tokenizeTagDsl(input);
  if (!lex.ok) return lex;
  if (lex.tokens.length === 0) {
    return { ok: false, error: 'Search expression is empty.', pos: 0 };
  }

  // Cursor state, captured by closures so the recursive helpers
  // can share it without threading explicit args.
  let i = 0;
  const tokens = lex.tokens;

  const peek = (): Token | undefined => tokens[i];

  /** Top-level — expr := and (OR and)* */
  const parseExpr = (): ParseTagDslResult => {
    let left = parseAnd();
    if (!left.ok) return left;
    while (peek()?.type === 'or') {
      i++; // consume OR
      const right = parseAnd();
      if (!right.ok) return right;
      left = { ok: true, ast: { kind: 'or', left: left.ast, right: right.ast } };
    }
    return left;
  };

  /** and := not (AND? not)* — AND is implicit between adjacent terms. */
  const parseAnd = (): ParseTagDslResult => {
    let left = parseNot();
    if (!left.ok) return left;
    while (true) {
      const tok = peek();
      if (!tok) break;
      if (tok.type === 'rparen' || tok.type === 'or') break;
      if (tok.type === 'and') i++; // consume explicit AND
      const right = parseNot();
      if (!right.ok) return right;
      left = { ok: true, ast: { kind: 'and', left: left.ast, right: right.ast } };
    }
    return left;
  };

  /** not := 'NOT' not | atom */
  const parseNot = (): ParseTagDslResult => {
    if (peek()?.type === 'not') {
      i++;
      const inner = parseNot();
      if (!inner.ok) return inner;
      return { ok: true, ast: { kind: 'not', inner: inner.ast } };
    }
    return parseAtom();
  };

  /** atom := '(' expr ')' | field | freeText */
  const parseAtom = (): ParseTagDslResult => {
    const tok = peek();
    if (!tok) {
      return { ok: false, error: 'Unexpected end of expression.', pos: input.length };
    }
    if (tok.type === 'lparen') {
      i++;
      const inner = parseExpr();
      if (!inner.ok) return inner;
      const close = peek();
      if (close?.type !== 'rparen') {
        return { ok: false, error: "Missing closing ')'.", pos: tok.pos };
      }
      i++;
      return inner;
    }
    // `word ':' value` (field) — only when the colon follows the
    // word with no whitespace between them in the original input.
    if (tok.type === 'word' || tok.type === 'quoted') {
      // Lookahead for a `key:value` field. Both `word` and `quoted`
      // can be the value side; only `word` can be the key side
      // (a quoted key would defeat the case-insensitive lowercase
      // rule).
      if (tok.type === 'word' && tokens[i + 1]?.type === 'colon' && (tokens[i + 2]?.type === 'word' || tokens[i + 2]?.type === 'quoted')) {
        const keyTok = tokens[i] as { type: 'word'; value: string; pos: number };
        const valTok = tokens[i + 2] as Token;
        const key = keyTok.value.toLowerCase();
        if (!FIELD_KEY_SET.has(key)) {
          return {
            ok: false,
            error: `Unknown field '${key}'. Supported fields: ${FIELD_KEYS.join(', ')}.`,
            pos: keyTok.pos,
          };
        }
        let value: string;
        if (valTok.type === 'word') value = valTok.value;
        else if (valTok.type === 'quoted') value = valTok.value;
        else return { ok: false, error: 'Field value expected.', pos: valTok.pos };
        i += 3;
        return { ok: true, ast: { kind: 'field', key, value } };
      }
      // Plain bare word / quoted string — free-text term.
      i++;
      return { ok: true, ast: { kind: 'free', value: tok.type === 'word' ? tok.value : tok.value } };
    }

    return { ok: false, error: `Unexpected token at this position.`, pos: tok.pos };
  };

  const result = parseExpr();
  if (!result.ok) return result;
  const trailing = peek();
  if (trailing) {
    return { ok: false, error: 'Unexpected trailing tokens.', pos: trailing.pos };
  }
  return result;
}

/**
 * Walk the AST and collect every `field` node into a flat
 * `{ key: values[] }` map. Convenient for the route layer's
 * "did this query mention `tag:foo`?" pre-flight check, and for
 * the UI's chip-rendering of an active query (chips per filter).
 *
 * NOT-ed fields are still surfaced (caller can inspect the AST
 * if it needs to distinguish positive / negative matches).
 */
export function collectFieldFilters(ast: TagDslAst): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  const walk = (node: TagDslAst) => {
    switch (node.kind) {
      case 'and':
      case 'or':
        walk(node.left);
        walk(node.right);
        return;
      case 'not':
        walk(node.inner);
        return;
      case 'field':
        if (!out[node.key]) out[node.key] = [];
        out[node.key].push(node.value);
        return;
      case 'free':
        return;
    }
  };
  walk(ast);
  return out;
}

/**
 * Walk the AST and collect every free-text term. Used by the
 * route layer to combine the DSL-side `name:` filter with the
 * legacy `?search=` parameter without surprising the caller.
 */
export function collectFreeTextTerms(ast: TagDslAst): string[] {
  const out: string[] = [];
  const walk = (node: TagDslAst) => {
    switch (node.kind) {
      case 'and':
      case 'or':
        walk(node.left);
        walk(node.right);
        return;
      case 'not':
        walk(node.inner);
        return;
      case 'free':
        out.push(node.value);
        return;
      case 'field':
        return;
    }
  };
  walk(ast);
  return out;
}
